/**
 * Sprint 5B — Reminder preview dialog (READ-ONLY).
 * Roept /admin/reminders/preview-single aan om HTML te renderen.
 * Klik op "Herinnering versturen" roept /admin/reminders/send-single aan.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, X, Info } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

const STAGE_LABELS = {
  pre_reminder: "Pre-reminder (3 dagen vóór)",
  on_due: "Op vervaldatum",
  overdue_7: "1e herinnering (+7)",
  overdue_14: "2e herinnering (+14)",
  escalation_30: "Interne escalatie (+30)",
};

function fmt(x, currency = "EUR") {
  const s = Number(x || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${currency} ${s}`;
}

export default function ReminderPreviewDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  defaultStage = "on_due",
  availableStages = ["pre_reminder", "on_due", "overdue_7", "overdue_14"],
  onSent,
}) {
  const [stage, setStage] = useState(defaultStage);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [overrideTo, setOverrideTo] = useState("");

  useEffect(() => {
    if (!open || !invoiceId) return;
    setStage(defaultStage);
    setOverrideTo("");
  }, [open, invoiceId, defaultStage]);

  useEffect(() => {
    if (!open || !invoiceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await adminApi.get(
          `/admin/reminders/preview-single/${invoiceId}`,
          { params: { stage_key: stage } },
        );
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, invoiceId, stage]);

  const handleSend = async () => {
    setSending(true);
    try {
      const payload = {
        invoice_id: invoiceId,
        stage_key: stage,
      };
      if (overrideTo.trim()) payload.override_to = overrideTo.trim();
      const r = await adminApi.post("/admin/reminders/send-single", payload);
      const status = r.data?.status;
      if (status === "sent") {
        toast.success(`Herinnering verzonden naar ${r.data?.to || preview?.recipient}`);
      } else if (status === "skipped_no_key") {
        toast.warning("E-mail overgeslagen: Resend API-sleutel ontbreekt op de server.");
      } else {
        toast.warning(`Status: ${status || "onbekend"}`);
      }
      onOpenChange(false);
      if (typeof onSent === "function") onSent(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const iframeSrcDoc = useMemo(() => {
    if (!preview?.html) return "";
    // Sandbox — geen scripts, geen forms, geen top navigation
    return preview.html;
  }, [preview]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl rounded-none max-h-[92vh] flex flex-col"
        data-testid="reminder-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo text-[#0A192F]">
            <Send className="w-5 h-5" />
            Herinnering preview — factuur {invoiceNumber || invoiceId?.slice(0, 8)}
          </DialogTitle>
        </DialogHeader>

        <div className="border-l-4 border-blue-500 bg-blue-50 p-2 text-xs text-blue-900 flex gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Deze preview is uitsluitend een render — er wordt niets verzonden
            of gelogd tot u op &quot;Herinnering versturen&quot; klikt.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Fase</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger
                className="rounded-none mt-1"
                data-testid="reminder-preview-stage-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableStages.map((k) => (
                  <SelectItem key={k} value={k}>
                    {STAGE_LABELS[k] || k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ontvanger (default = klant e-mail)</Label>
            <Input
              type="email"
              value={overrideTo}
              onChange={(e) => setOverrideTo(e.target.value)}
              placeholder={preview?.recipient || "klant@voorbeeld.nl"}
              className="rounded-none mt-1"
              data-testid="reminder-preview-override-to"
            />
          </div>
          <div className="text-xs" data-testid="reminder-preview-summary">
            <div><strong>Openstaand:</strong> {fmt(preview?.amount_due, preview?.currency)}</div>
            <div><strong>Vervaldatum:</strong> {preview?.due_date || "—"}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-6 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Preview laden...
          </div>
        ) : preview ? (
          <>
            <div className="text-xs border border-gray-200 bg-gray-50 p-2 space-y-1">
              <div><strong>Onderwerp:</strong> {preview.subject}</div>
              <div><strong>Aan:</strong> {overrideTo.trim() || preview.recipient || "(geen)"}</div>
              <div><strong>Bijlage:</strong> {preview.attachments_preview?.[0]?.filename || "—"}</div>
            </div>
            <div
              className="flex-1 border border-gray-200 overflow-hidden"
              style={{ minHeight: "300px" }}
            >
              <iframe
                srcDoc={iframeSrcDoc}
                sandbox=""
                title="reminder-preview"
                className="w-full h-full min-h-[300px]"
                data-testid="reminder-preview-iframe"
              />
            </div>
          </>
        ) : (
          <div className="p-6 text-sm text-gray-500 italic">
            Geen preview beschikbaar.
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
            data-testid="reminder-preview-cancel"
            disabled={sending}
          >
            <X className="w-4 h-4 mr-1" /> Annuleren
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !preview}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="reminder-preview-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Herinnering versturen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
